import apiClient from '../../api/apiClient';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SubscriptionPlan {
  id: number;
  name: string;
  description?: string;
  stripe_product_id?: string;
  stripe_price_id?: string;
  price: number;
  currency: string;
  interval: string;
  interval_count: number;
  trial_days: number;
  features?: any[];
  max_chatbots?: number;
  max_messages_per_month?: number;
  max_knowledge_sources?: number;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionPlanCreate {
  name: string;
  description?: string;
  price: number;
  currency?: string;
  interval?: string;
  interval_count?: number;
  trial_days?: number;
  features?: any[];
  max_chatbots?: number;
  max_messages_per_month?: number;
  max_knowledge_sources?: number;
  is_active?: boolean;
  sort_order?: number;
}

export interface SubscriptionPlanUpdate {
  name?: string;
  description?: string;
  price?: number;
  currency?: string;
  interval?: string;
  interval_count?: number;
  trial_days?: number;
  features?: any[];
  max_chatbots?: number;
  max_messages_per_month?: number;
  max_knowledge_sources?: number;
  is_active?: boolean;
  sort_order?: number;
}

export interface Subscription {
  id: number;
  company_id: number;
  plan_id?: number;
  user_id?: number;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  status: string;
  current_period_start?: string;
  current_period_end?: string;
  cancel_at_period_end: boolean;
  canceled_at?: string;
  trial_start?: string;
  trial_end?: string;
  created_at: string;
  updated_at: string;
  plan?: SubscriptionPlan;
  company_name?: string;
  user_name?: string;
}

export interface Payment {
  id: number;
  subscription_id?: number;
  company_id: number;
  user_id?: number;
  stripe_invoice_id?: string;
  stripe_payment_intent_id?: string;
  stripe_charge_id?: string;
  amount: number;
  currency: string;
  status: string;
  invoice_url?: string;
  invoice_pdf?: string;
  paid_at?: string;
  stripe_refund_id?: string;
  refunded_at?: string;
  refund_amount?: number;
  created_at: string;
  updated_at: string;
  company_name?: string;
  user_name?: string;
  plan_name?: string;
}

export interface PaymentStats {
  total_revenue: number;
  currency: string;
  paid_count: number;
  paid_total: number;
  refunded_count: number;
  refunded_total: number;
  failed_count: number;
  failed_total: number;
  pending_count: number;
  pending_total: number;
}

export interface SubscriptionInvitationCreate {
  user_id: number;
  plan_id: number;
}

export interface SubscriptionInvitation {
  id: number;
  token: string;
  company_id: number;
  plan_id: number;
  email: string;
  status: string;
  expires_at: string;
  accepted_at?: string;
  created_at: string;
  company_name?: string;
  plan_name?: string;
  user_name?: string;
}

export interface DashboardStats {
  active_subscriptions: number;
  trialing_subscriptions: number;
  past_due_subscriptions: number;
  canceled_subscriptions: number;
  total_revenue: number;
  mrr: number;
  currency: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    current_page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

const SUBSCRIPTIONS_ENDPOINT = '/subscriptions';
const ADMIN_ENDPOINT = '/admin';

const subscriptionsService = {
  // Owner endpoints
  getPlans: async (): Promise<SubscriptionPlan[]> => {
    return apiClient.get(`${SUBSCRIPTIONS_ENDPOINT}/plans`);
  },

  getCurrentSubscription: async (): Promise<Subscription> => {
    return apiClient.get(`${SUBSCRIPTIONS_ENDPOINT}/current`);
  },

  createCheckout: async (planId: number): Promise<{ checkout_url: string }> => {
    return apiClient.post(`${SUBSCRIPTIONS_ENDPOINT}/checkout`, { plan_id: planId });
  },

  createPortalSession: async (): Promise<{ portal_url: string }> => {
    return apiClient.post(`${SUBSCRIPTIONS_ENDPOINT}/portal`);
  },

  changePlan: async (planId: number): Promise<Subscription> => {
    return apiClient.post(`${SUBSCRIPTIONS_ENDPOINT}/change-plan`, { plan_id: planId });
  },

  verifyCheckoutSession: async (sessionId: string): Promise<Subscription> => {
    return apiClient.post(`${SUBSCRIPTIONS_ENDPOINT}/verify-session?session_id=${encodeURIComponent(sessionId)}`);
  },

  getPayments: async (params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<Payment>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return apiClient.get(`${SUBSCRIPTIONS_ENDPOINT}/payments${qs}`);
  },

  // Admin endpoints
  adminGetPlans: async (): Promise<SubscriptionPlan[]> => {
    return apiClient.get(`${ADMIN_ENDPOINT}/plans`);
  },

  adminCreatePlan: async (data: SubscriptionPlanCreate): Promise<SubscriptionPlan> => {
    return apiClient.post(`${ADMIN_ENDPOINT}/plans`, data);
  },

  adminUpdatePlan: async (id: number, data: SubscriptionPlanUpdate): Promise<SubscriptionPlan> => {
    return apiClient.put(`${ADMIN_ENDPOINT}/plans/${id}`, data);
  },

  adminDeletePlan: async (id: number): Promise<SubscriptionPlan> => {
    return apiClient.delete(`${ADMIN_ENDPOINT}/plans/${id}`);
  },

  adminGetSubscriptions: async (params?: {
    page?: number;
    page_size?: number;
    status?: string;
    company_id?: number;
    plan_id?: number;
  }): Promise<PaginatedResponse<Subscription>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.company_id) queryParams.append('company_id', params.company_id.toString());
    if (params?.plan_id) queryParams.append('plan_id', params.plan_id.toString());
    const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return apiClient.get(`${ADMIN_ENDPOINT}/subscriptions${qs}`);
  },

  adminGetSubscriptionDetail: async (id: number): Promise<Subscription> => {
    return apiClient.get(`${ADMIN_ENDPOINT}/subscriptions/${id}`);
  },

  adminCancelSubscription: async (id: number, atPeriodEnd: boolean = true): Promise<Subscription> => {
    return apiClient.post(`${ADMIN_ENDPOINT}/subscriptions/${id}/cancel?at_period_end=${atPeriodEnd}`);
  },

  adminGetPayments: async (params?: {
    page?: number;
    page_size?: number;
    status?: string;
    company_id?: number;
  }): Promise<PaginatedResponse<Payment>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.company_id) queryParams.append('company_id', params.company_id.toString());
    const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return apiClient.get(`${ADMIN_ENDPOINT}/payments${qs}`);
  },

  adminGetStats: async (): Promise<DashboardStats> => {
    return apiClient.get(`${ADMIN_ENDPOINT}/dashboard/stats`);
  },

  adminRefundPayment: async (paymentId: number, data?: { reason?: string; amount?: number }): Promise<Payment> => {
    return apiClient.post(`${ADMIN_ENDPOINT}/payments/${paymentId}/refund`, data || {});
  },

  adminGetPaymentStats: async (): Promise<PaymentStats> => {
    return apiClient.get(`${ADMIN_ENDPOINT}/payments/stats`);
  },

  adminSendInvoiceEmail: async (paymentId: number): Promise<{ message: string }> => {
    return apiClient.post(`${ADMIN_ENDPOINT}/payments/${paymentId}/send-invoice`);
  },

  adminCreateInvitation: async (data: SubscriptionInvitationCreate): Promise<SubscriptionInvitation> => {
    return apiClient.post(`${ADMIN_ENDPOINT}/invitations`, data);
  },

  adminGetInvitations: async (params?: {
    page?: number;
    page_size?: number;
    status?: string;
  }): Promise<PaginatedResponse<SubscriptionInvitation>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.status) queryParams.append('status', params.status);
    const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return apiClient.get(`${ADMIN_ENDPOINT}/invitations${qs}`);
  },

  acceptInvitation: async (token: string): Promise<{ checkout_url: string }> => {
    return apiClient.post(`${SUBSCRIPTIONS_ENDPOINT}/accept-invitation?token=${encodeURIComponent(token)}`);
  },
};

export default subscriptionsService;
